/**
 * Lot 381 — Source unique de vérité pour les tarifs publics SPV.
 *
 * Cette constante est référencée dans :
 *   - `/pages/public/HomePage.js` (section pricing)
 *   - `/pages/public/SpvOfferPage.js` (tarifs offre cœur)
 *   - tout futur bloc tarifaire public
 *
 * Règle : **ne jamais hardcoder un tarif SPV ailleurs.** Toujours importer
 * `PRICING` depuis ce fichier pour garantir la cohérence économique.
 *
 * Tous les montants sont exprimés **Hors Taxes**.
 */

export const PRICING = {
  /** Création / structuration initiale du véhicule (SPV / SCSp / SAS). */
  spvCreation: {
    amount: 4000,
    currency: 'EUR',
    period: 'one_off',
    labelFr: 'Création / structuration initiale de la\u00A0SPV',
    labelEn: 'SPV creation / initial structuring',
    cadenceFr: 'À la constitution\u00A0— HT',
    cadenceEn: 'One-off at setup — excl. VAT',
    testId: 'spv-price-creation',
  },
  /** Administration annuelle : domiciliation + comptabilité + juridique récurrent. */
  spvAnnualAdmin: {
    amount: 2000,
    currency: 'EUR',
    period: 'per_year',
    labelFr: 'Domiciliation\u00A0+ comptabilité\u00A0+ juridique',
    labelEn: 'Domiciliation + accounting + legal',
    cadenceFr: 'Par an\u00A0— HT',
    cadenceEn: 'Per year — excl. VAT',
    testId: 'spv-price-admin',
  },
  /** Clôture / dissolution du véhicule. */
  spvClosing: {
    amount: 1000,
    currency: 'EUR',
    period: 'one_off',
    labelFr: 'Clôture de la\u00A0SPV',
    labelEn: 'SPV closing',
    cadenceFr: 'À la dissolution\u00A0— HT',
    cadenceEn: 'One-off at dissolution — excl. VAT',
    testId: 'spv-price-closing',
  },
};

/**
 * Formate un montant entier en "X XXX €" (style FR).
 * Ne rajoute pas "HT" — à utiliser dans les cadences ou les subtitles.
 */
export function formatPrice(amount, locale = 'fr') {
  const formatter = new Intl.NumberFormat(
    locale === 'fr' ? 'fr-FR' : 'en-GB',
    { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, minimumFractionDigits: 0 },
  );
  return formatter.format(amount);
}

/**
 * Format prêt à l'emploi pour les PriceCard : "4 000 €".
 */
export function priceLabel(pricingKey, locale = 'fr') {
  const p = PRICING[pricingKey];
  if (!p) return '—';
  return formatPrice(p.amount, locale);
}

/**
 * Phrase récapitulative courte utilisée sur la homepage + CTA secondaire.
 * Ex. FR : "Création / structuration : 4 000 € HT · Administration : 2 000 € HT / an · Clôture : 1 000 € HT"
 */
export function pricingSummary(locale = 'fr') {
  if (locale === 'en') {
    return `Creation / structuring: ${formatPrice(PRICING.spvCreation.amount, 'en')} excl. VAT · Annual admin: ${formatPrice(PRICING.spvAnnualAdmin.amount, 'en')} excl. VAT / year · Closing: ${formatPrice(PRICING.spvClosing.amount, 'en')} excl. VAT`;
  }
  return `Création / structuration : ${formatPrice(PRICING.spvCreation.amount)} HT · Administration : ${formatPrice(PRICING.spvAnnualAdmin.amount)} HT / an · Clôture : ${formatPrice(PRICING.spvClosing.amount)} HT`;
}

/**
 * Bandeau anti-confusion "pas de frais de levée de fonds" —
 * wording homogène sur toutes les pages publiques.
 */
export const NO_FUNDRAISING_FEE_COPY = {
  fr: "Aucun frais de levée de fonds n'est prélevé par défaut sur le capital apporté par les investisseurs.",
  en: "No fundraising fee is charged by default on the capital brought in by investors.",
};
